import { Alert, Autocomplete, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import { jobBrokerService, workflowService } from "../../../Core/Api/services";
import { ApplyRuleOnObjectsRequest } from "../../../Core/Api/Types/apply-rule-on-objects-request";
import { CreateInstanceRequest } from "../../../Core/Api/Types/create-instance-request";
import { DbJobRuleInfos } from "../../../Core/Api/Types/db-job-rule-infos";
import { DbWorkflowDefinitionInfos } from "../../../Core/Api/Types/db-workflow-definition-infos";
import { GetRulesMatchingObjectRequest } from "../../../Core/Api/Types/get-rules-request";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

export type ModalApplyJobOrWorkflowElementProps = {
  isJob: boolean;
  assetIds: number[];
  onSuccess?: () => void;
};

export type ModalApplyJobOrWorkflowProps = CommonModalProps<ModalApplyJobOrWorkflowElementProps>;

const ModalApplyJobOrWorkflow = React.forwardRef<any, ModalApplyJobOrWorkflowProps>((props, ref) => {
  const { closeModal, onSuccess, ...modalProps } = props;
  const { assetIds, isJob } = modalProps;
  const customSnackbar = useCustomSnackbar();
  const { t } = useTranslation();

  const [rules, setRules] = React.useState<DbJobRuleInfos[]>();
  const [definitions, setDefinitions] = React.useState<DbWorkflowDefinitionInfos[]>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [jobRule, setJobRule] = React.useState<DbJobRuleInfos>();
  const [workflowDef, setWorkflowDef] = React.useState<DbWorkflowDefinitionInfos>();

  const handleCancelClick = () => {
    closeModal();
  };

  const handleConfirmClick = () => {
    if (isJob) {
      let ruleId = jobRule?.id;

      if (ruleId) {
        let request: ApplyRuleOnObjectsRequest = {
          ruleId: ruleId,
          objectIds: assetIds,
        };

        jobBrokerService.ApplyRuleOnObjects(request).then((response) => {
          if (response.success) {
            customSnackbar(t("alert:success.jobRuleApplied", { count: assetIds.length }), "success");

            onSuccess && onSuccess();
          } else customSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
        });
      }
    } else {
      let requests: CreateInstanceRequest[] = [];

      assetIds.forEach((assetId) => {
        requests.push({
          assetId: assetId,
          annotation: "By Web UI Apply Definition",
          definitionType: workflowDef!.type,
          definitionId: workflowDef!.id,
        });
      });

      workflowService.CreateInstances(requests).then((response) => {
        if (response.success) {
          customSnackbar(t("alert:success.workflowApplied", { count: assetIds.length }), "success");

          onSuccess && onSuccess();
        } else customSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
      });
    }

    closeModal();
  };

  React.useEffect(() => {
    if (isJob) {
      let request: GetRulesMatchingObjectRequest = {
        objectIds: assetIds,
      };
      jobBrokerService.ListRulesByObjects(request).then((response) => {
        if (response.rules) setRules(response.rules);
        setLoading(false);
      });
    } else {
      // Should be reviewed for a multi objectIds API call
      workflowService.GetDefinitionsByObject(assetIds[0]).then((response) => {
        let defsResponse = response.definitions;
        if (defsResponse !== null) setDefinitions(defsResponse);
        setLoading(false);
      });
    }
  }, []);

  return (
    <Modal
      title={
        isJob
          ? assetIds.length === 1
            ? t("modal:title.applyJobForAsset", { assetId: assetIds[0] })
            : t("modal:title.applyJobForMultipleAssets")
          : assetIds.length === 1
          ? t("modal:title.applyWorkflowForAsset", { assetId: assetIds[0] })
          : t("modal:title.applyWorkflowForMultipleAssets")
      }
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            disabled={
              isJob ? jobRule === null || jobRule === undefined : workflowDef === null || workflowDef === undefined
            }
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
          >
            {isJob ? t("button:applyJobRule") : t("button:applyDefinitionAndCreateWorkflow")}
          </Button>
        </Stack>
      }
    >
      <Stack>
        {!loading && (
          <>
            {((rules && rules?.length > 0) || (definitions && definitions?.length > 0)) && (
              <Typography variant="h6" component="span" color="text.secondary">
                {isJob ? t("modal:body.selectJobRule") : t("modal:body.selectWorkflowDefinition")}
              </Typography>
            )}
            <Stack>
              {isJob && rules && rules.length > 0 && (
                <Autocomplete
                  onChange={(e, value) => {
                    value !== null ? setJobRule(value) : setJobRule(undefined);
                  }}
                  disabled={rules?.length === 0}
                  options={loading ? [] : (rules as DbJobRuleInfos[])}
                  getOptionLabel={(option) => option.name as string}
                  fullWidth
                  renderInput={(params) => <TextField {...params} placeholder={t("field:placeholder.selectJobRule")} />}
                />
              )}

              {!isJob && definitions && definitions.length > 0 && (
                <Autocomplete
                  onChange={(e, value) => {
                    value !== null ? setWorkflowDef(value) : setWorkflowDef(undefined);
                  }}
                  disabled={definitions?.length === 0}
                  options={loading ? [] : (definitions as DbWorkflowDefinitionInfos[])}
                  getOptionLabel={(option) => option.name as string}
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} placeholder={t("field:placeholder.selectWorkflow")} />
                  )}
                />
              )}
            </Stack>
            {isJob && rules?.length === 0 && <Alert severity="warning">{t("alert:warning.noMatchingRules")}</Alert>}
            {!isJob && definitions?.length === 0 && (
              <Alert severity="warning">{t("alert:warning.noMatchingWorkflows")}</Alert>
            )}
          </>
        )}

        {loading && <CircularProgress />}
      </Stack>
    </Modal>
  );
});

export default ModalApplyJobOrWorkflow;
